import { useQueryClient } from "@tanstack/react-query";
import { Outlet, useRouter } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { TaxAuthShell } from "~/components/auth/tax-auth-shell";
import { TaxSignInForm } from "~/components/auth/tax-sign-in-form";
import { AppShell } from "~/components/layout/app-shell";
import { WorkspaceErrorShell } from "~/components/layout/workspace-error-shell";
import { WorkspaceLoadingShell } from "~/components/layout/workspace-loading-shell";
import { SessionReplayNotice } from "~/components/replay/session-replay-notice";
import { CommitVerifyingBanner } from "~/components/ui/commit-verifying-banner";
import { StateBlock } from "~/components/ui/states";
import { BUILD_VERSION } from "~/config/build-version";
import { useT } from "~/i18n";
import { CommercialRouteBoundary } from "~/lib/access/commercial-route-boundary";
import { SessionProvider } from "~/lib/access/session-context";
import { useApi } from "~/lib/api/api-context";
import { useSessionQuery } from "~/lib/api/queries";
import { useAuth } from "~/lib/auth/auth-context";
import { installSessionReplay } from "~/lib/replay";

/**
 * Gate between "we have a browser tab" and "we have an authenticated
 * principal with a permission set". Each failure mode gets its own screen:
 * misconfiguration is not a login failure, and a login failure is not a
 * permissions problem.
 */
export function RootLayout() {
  const t = useT();
  const auth = useAuth();
  const api = useApi();
  const router = useRouter();
  const queryClient = useQueryClient();
  const sessionQuery = useSessionQuery({ enabled: auth.status === "authenticated" });

  // Sign-out now stays in the SPA. Remove every user-scoped query immediately
  // so a second operator cannot see the first operator's cached session/data
  // while their own Cognito identity is being established.
  useEffect(() => {
    if (auth.status === "anonymous" || auth.status === "error") {
      queryClient.clear();
    }
  }, [auth.status, queryClient]);

  useEffect(() => {
    if (auth.status !== "authenticated" || !sessionQuery.data) return;
    return installSessionReplay({ api, router, buildVersion: BUILD_VERSION });
  }, [auth.status, sessionQuery.data, api, router]);

  if (auth.status === "unconfigured") {
    return (
      <PreSessionShell title={t("auth.error.title")}>
        <StateBlock
          kind="unavailable"
          title={t("auth.error.title")}
          body={t("auth.error.config", { reason: auth.reason ?? "—" })}
        />
      </PreSessionShell>
    );
  }

  if (auth.status === "initialising" || auth.status === "reconnecting") {
    return (
      <WorkspaceLoadingShell
        label={t(auth.status === "reconnecting" ? "auth.loading.reconnect" : "auth.signingIn")}
        onRetry={auth.status === "reconnecting" ? auth.retrySession : undefined}
        onSignOut={auth.signOut}
      />
    );
  }

  if (auth.status === "anonymous" || auth.status === "error") {
    return (
      <PreSessionShell
        title={t("auth.accessTitle")}
        description={t("auth.loginDescription")}
        note={t("auth.domainHint")}
      >
        <TaxSignInForm />
      </PreSessionShell>
    );
  }

  if (sessionQuery.isPending) {
    return (
      <WorkspaceLoadingShell
        label={t("common.loading")}
        onRetry={() => void sessionQuery.refetch()}
      />
    );
  }

  if (sessionQuery.isError) {
    return (
      <WorkspaceErrorShell
        error={sessionQuery.error}
        onRetry={() => void sessionQuery.refetch()}
        session={sessionQuery.data}
      />
    );
  }

  return (
    <SessionProvider session={sessionQuery.data}>
      <AppShell>
        <CommitVerifyingBanner />
        <CommercialRouteBoundary>
          <Outlet />
        </CommercialRouteBoundary>
        <SessionReplayNotice />
      </AppShell>
    </SessionProvider>
  );
}

/** Pre-session chrome: no navigation, because nothing is navigable yet. */
function PreSessionShell({
  title,
  description,
  note,
  children,
  variant = "default",
}: {
  title: string;
  description?: string;
  note?: string;
  children: ReactNode;
  variant?: "default" | "loading";
}) {
  return (
    <SessionProvider session={null}>
      <TaxAuthShell title={title} description={description} note={note} variant={variant}>
        {children}
      </TaxAuthShell>
    </SessionProvider>
  );
}
